import * as Sentry from '@sentry/vue'
import { ref } from 'vue'

// Recovery from a lazy route chunk that fails to load (#730, #3874).
//
// Vite emits content-hashed chunk filenames. On Cloudflare Pages a request for
// a MISSING /assets/*.js is answered with the SPA index fallback — 200
// text/html — and the zone serves it with `cache-control: max-age=14400`. So a
// chunk requested while a deploy is propagating gets HTML cached against its
// URL for four hours, and every later import() of it (or of any chunk that
// imports it — the browser error names the importer, not the broken file)
// fails. A plain reload does not help: the browser serves the poisoned entry
// from HTTP cache without touching the network.
//
// What recovers is re-fetching every same-origin /assets/ URL the page has
// loaded or tried to load with `cache: 'reload'`, which overwrites the cache
// entries, and only then reloading. If that still fails the app renders a
// "Vi fikk ikke lastet siden" panel (App.vue, on `chunkLoadFailed`) instead of
// a blank page.

// True once a chunk failure survived the evict-and-reload attempt. App.vue
// swaps the router view for a load-error panel while it is set; the router's
// afterEach clears it on the next successful navigation.
export const chunkLoadFailed = ref(false)

export const RELOAD_KEY = 'stemne:chunk-reload-at'

// A second failure within this window of the last recovery reload means the
// reload did not help — stop reloading and show the error panel. Generous on
// purpose: eviction plus a slow mobile page load can take well over 5s, and a
// window shorter than that lets a still-failing page reload forever.
export const RELOAD_GUARD_MS = 30_000

// Upper bound on eviction, so a slow network never holds up the reload.
export const EVICT_TIMEOUT_MS = 3_000

// isDynamicImportError detects the browser-specific error messages thrown when
// a dynamically imported chunk fails to load, plus Vite's own error when a
// route's CSS chunk fails to preload (a poisoned .css blanks the route just
// the same). Pinned by router/__tests__/isDynamicImportError.spec.ts.
export function isDynamicImportError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err)
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') || // Safari shape
    message.includes('error loading dynamically imported module') || // Firefox shape
    message.includes('Unable to preload CSS for') // Vite's CSS preload helper
  )
}

// The same-origin /assets/ URLs to re-fetch: every resource the page loaded or
// tried to load (cache-served and failed module loads both appear in the
// resource timeline, transitive dependencies included), every asset the DOM
// references, plus the URL named in the error message. Deduped.
//
// The DOM sweep matters on long-lived tabs: the resource-timing buffer is
// capped (250 entries in Chromium) and API calls fill it, so the poisoned
// chunk may be missing from the timeline. Vite's preload helper injects a
// <link rel="modulepreload"> for every dependency chunk before importing it,
// and the element stays in <head> even when the fetch fails.
export function assetUrlsToEvict(err?: unknown): string[] {
  const origin = window.location.origin
  const urls = new Set<string>()
  const add = (raw: string) => {
    try {
      const u = new URL(raw)
      if (u.origin === origin && u.pathname.startsWith('/assets/')) urls.add(u.href)
    } catch {
      // Not a URL — ignore.
    }
  }

  if (typeof performance !== 'undefined' && typeof performance.getEntriesByType === 'function') {
    for (const entry of performance.getEntriesByType('resource')) add(entry.name)
  }

  if (typeof document !== 'undefined') {
    const els = document.querySelectorAll<HTMLLinkElement | HTMLScriptElement>(
      'link[rel="modulepreload"][href], link[rel="stylesheet"][href], script[src]',
    )
    for (const el of els) add(el instanceof HTMLScriptElement ? el.src : el.href)
  }

  const message = err instanceof Error ? err.message : err == null ? '' : String(err)
  const match = message.match(/https?:\/\/\S+/)
  if (match) add(match[0])

  return [...urls]
}

// Overwrite the browser's cached copy of every asset URL with a fresh network
// fetch. Never throws and never takes longer than `timeoutMs`.
export async function evictAssetCache(
  err?: unknown,
  timeoutMs: number = EVICT_TIMEOUT_MS,
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    if (typeof fetch !== 'function') return
    const urls = assetUrlsToEvict(err)
    if (urls.length === 0) return

    const controller = new AbortController()
    const timedOut = new Promise<void>((resolve) => {
      timer = setTimeout(() => {
        controller.abort()
        resolve()
      }, timeoutMs)
    })
    // Read each body to the end so the fresh response is fully written to the
    // cache before the page reloads.
    const refetches = Promise.allSettled(
      urls.map((u) =>
        fetch(u, { cache: 'reload', signal: controller.signal }).then((r) => r.arrayBuffer()),
      ),
    )
    await Promise.race([refetches, timedOut])
  } catch {
    // Best effort — the reload that follows still runs.
  } finally {
    clearTimeout(timer)
  }
}

export interface ChunkRecoveryDeps {
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  now?: () => number
  evict?: (err: unknown) => Promise<void>
  navigate?: (href: string) => void
  capture?: (err: unknown) => void
}

// The router.onError handler. First failure: evict the asset cache, then do a
// full reload to the intended destination. A second failure within
// RELOAD_GUARD_MS: stop, and let App.vue render the load-error panel.
export async function handleChunkLoadError(
  err: unknown,
  to: { fullPath: string },
  deps: ChunkRecoveryDeps = {},
): Promise<void> {
  if (!isDynamicImportError(err)) return

  const storage = deps.storage ?? window.sessionStorage
  const now = deps.now ?? Date.now

  const last = Number(storage.getItem(RELOAD_KEY) || 0)
  if (last && now() - last < RELOAD_GUARD_MS) {
    storage.removeItem(RELOAD_KEY)
    chunkLoadFailed.value = true
    // Report explicitly: vue-router swallows navigation errors (the initial
    // push and RouterLink both .catch() them), so without this a visitor stuck
    // on the panel is invisible to us.
    ;(deps.capture ?? Sentry.captureException)(err)
    return
  }

  await (deps.evict ?? evictAssetCache)(err)
  // Stamp AFTER eviction so the time it took doesn't eat the guard window.
  storage.setItem(RELOAD_KEY, String(now()))
  const navigate =
    deps.navigate ??
    ((href: string) => {
      window.location.href = href
    })
  navigate(to.fullPath)
}

// Called on every successful navigation: hide the panel and drop the guard
// stamp, so a later, unrelated chunk failure in this tab gets a fresh
// evict-and-reload instead of going straight to the panel. Cannot loop — the
// stamp only clears after a navigation that actually worked.
export function clearChunkRecoveryState(
  storage: Pick<Storage, 'removeItem'> = window.sessionStorage,
): void {
  chunkLoadFailed.value = false
  storage.removeItem(RELOAD_KEY)
}

// The load-error panel's retry: evict, stamp the guard, reload. Stamping here
// means a reload that still fails goes straight back to the panel — one
// attempt per click — instead of the handler spending a second evict+reload.
export async function retryAfterChunkFailure(deps: ChunkRecoveryDeps = {}): Promise<void> {
  const storage = deps.storage ?? window.sessionStorage
  const now = deps.now ?? Date.now
  await (deps.evict ?? evictAssetCache)(undefined)
  storage.setItem(RELOAD_KEY, String(now()))
  const navigate =
    deps.navigate ??
    (() => {
      window.location.reload()
    })
  navigate(window.location.href)
}
